import {
  ArchivedDocument,
  CollectionReference,
  DocumentArchive,
  DocumentReference,
  Firestore,
  IReffable,
  subCollection,
  WithRef,
} from '@principle-theorem/shared';
import {
  IAIReceptionistKnowledgeBase,
  IPractice,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import { Practice } from '@principle-theorem/principle-core';

export class AIReceptionistKnowledgeBaseService {
  /**
   * Get the current AI Receptionist Knowledge Base for a practice.
   * Returns undefined if no knowledge base exists.
   */
  static async getKnowledgeBase(
    practice: IReffable<IPractice>
  ): Promise<WithRef<IAIReceptionistKnowledgeBase> | undefined> {
    const docs = await Firestore.getDocs(
      Practice.aiReceptionistKnowledgeBaseCol(practice)
    );
    if (docs.length === 0) {
      return undefined;
    }
    return docs[0];
  }

  /**
   * Save or update the AI Receptionist Knowledge Base for a practice.
   * If it already exists, archives the current version before saving.
   */
  static async saveKnowledgeBase(
    practice: IReffable<IPractice>,
    aiKnowledgeBase: IAIReceptionistKnowledgeBase,
    stafferRef?: DocumentReference<IStaffer>
  ): Promise<DocumentReference<IAIReceptionistKnowledgeBase>> {
    const existing =
      await AIReceptionistKnowledgeBaseService.getKnowledgeBase(practice);

    if (existing) {
      await DocumentArchive.snapshotToArchive(
        existing,
        AIReceptionistKnowledgeBaseService.archiveCol(existing),
        undefined,
        stafferRef
      );
      await Firestore.saveDoc({
        ...existing,
        ...aiKnowledgeBase,
        ref: existing.ref,
      });
      return existing.ref;
    }

    return Firestore.addDoc(
      Practice.aiReceptionistKnowledgeBaseCol(practice),
      aiKnowledgeBase
    );
  }

  /**
   * Get archived versions of the AI Receptionist Knowledge Base, sorted by archivedAt descending.
   */
  static async getKnowledgeBaseVersions(
    practice: IReffable<IPractice>
  ): Promise<ArchivedDocument<WithRef<IAIReceptionistKnowledgeBase>>[]> {
    const existing =
      await AIReceptionistKnowledgeBaseService.getKnowledgeBase(practice);
    if (!existing) {
      return [];
    }
    const versions = await Firestore.getDocs(
      AIReceptionistKnowledgeBaseService.archiveCol(existing)
    );
    return versions
      .sort((versionA, versionB) =>
        DocumentArchive.sortByArchivedAt(versionA, versionB)
      )
      .reverse();
  }

  /**
   * Archive subcollection for an AI Receptionist Knowledge Base document.
   */
  static archiveCol(
    aiKnowledgeBase: WithRef<IAIReceptionistKnowledgeBase>
  ): CollectionReference<ArchivedDocument<IAIReceptionistKnowledgeBase>> {
    return subCollection<ArchivedDocument<IAIReceptionistKnowledgeBase>>(
      aiKnowledgeBase.ref,
      'archive'
    );
  }
}
